import {Prettify} from '@practicegenius/pg-util/src/TypescriptUtils';
import {SocialAuthIntegration} from '../data/integrations';
import {GlobalUserProfile} from '../data/users/GlobalUser';
import {MobilePushNotification} from './mobile-push-notifications';
import {DirectSocialSharePayload} from './social-sharing';

export type MobileAppMessageChannel =
  | 'generic'
  | 'login'
  | 'socialAuth'
  | 'pushNotifications'
  | 'debug'
  | 'recaptcha';

type NewFeature = 'recaptcha';

export type GenericAppToSitesMessage = Prettify<
  {channel: 'generic'} & (
    | {
        type: 'receive_shared_content_code';
        payload: {
          shareCode: string;
        };
      }
    | {
        type: 'handle_mobile_logout';
        payload: {
          deviceType: string;
          token: string | null;
        };
      }
    | {
        type: 'supports_feature';
        payload: NewFeature;
      }
  )
>;

export type DebugAppToSitesMessage = {
  channel: 'debug';
  type: 'receive_app_debug_info';
  payload: {
    isDebug: boolean;
  };
};

export type PushNotificationsAppToSitesMessage = Prettify<
  {channel: 'pushNotifications'} & (
    | {
        type: 'receive_register_push_notification_token';
        payload: {
          deviceType: string;
          token: string;
          previousTokens?: string[];
        };
      }
    | {
        type: 'receive_mobile_push_notification';
        payload: {
          notification: MobilePushNotification;
        };
      }
  )
>;

export type SocialAuthAppToSitesMessage = {
  channel: 'socialAuth';
  type: 'receive_social_auth_success';
  payload: {
    platform: SocialAuthIntegration;
    /** This contains important metadata needed to connect the registering user to a practice user record. */
    extraData: Record<string, unknown>;
    token: string;
  };
};

export type ReceiveRecaptchaTokenAppToSitesMessage = {
  channel: 'recaptcha';
  type: 'receive_recaptcha_token';
  payload: {
    recaptchaToken: string;
    platform: 'ios' | 'android';
  };
};

export type AppToSitesMessage =
  | DebugAppToSitesMessage
  | GenericAppToSitesMessage
  | PushNotificationsAppToSitesMessage
  | SocialAuthAppToSitesMessage
  | ReceiveRecaptchaTokenAppToSitesMessage;

export type ChannelUpdateSitesToAppMessage = {
  type: 'channel_update';
  payload: {
    channel: MobileAppMessageChannel;
    ready: boolean;
  };
};

export const makeChannelUpdateMessage = (
  channel: MobileAppMessageChannel,
  ready: boolean,
) =>
  ({
    type: 'channel_update',
    payload: {
      channel,
      ready,
    },
  }) as ChannelUpdateSitesToAppMessage;

/** Sites-dot asks the app to open the URL in an external browser */
export type RequestOpenExternalUrlSitesToAppMessage = {
  type: 'request_open_external_url';
  payload: {
    url: string;
  };
};

/** Sites-dot sends the user profile to the mobile app */
export type ReceiveUserProfileSitesToAppMessage = {
  type: 'receive_user_profile';
  payload: GlobalUserProfile & {
    /** The notification data for the active practice user */
    notificationCounts: {
      messages: number;
      appointments: number;
    };
    cookie: string;
  };
};

/** Sites-dot asks the mobile app to initiate a native social auth workflow with the given provider */
export type RequestSocialAuthSitesToAppMessage = {
  type: 'request_social_auth';
  payload: {
    platform: SocialAuthIntegration;
  };
};

/** Sites-dot asks the mobile app for app debug info */
export type RequestDebugInfoSitesToAppMessage = {
  type: 'request_app_debug_info';
};

/** Sites-dot asks the mobile app to open the debug menu */
export type OpenMobileDebugSitesToAppMessage = {
  type: 'open_mobile_debug';
};

/** Sites-dot asks the mobile app to perform its logout steps */
export type RequestLogoutSitesToAppMessage = {
  type: 'request_logout';
};

/** Sites-dot informs the mobile app of the user's authentication status */
export type NotifyUserAuthenticationStatusSiteToAppMessage = {
  type: 'notify_user_authenticated_status';
  payload: {
    authenticated: boolean;
  };
};

export type RequestScreenWakeLockSitesToAppMessage = {
  type: 'request_screen_wake_lock';
  payload: {
    enabled: boolean;
  };
};

/** Old messages sent by sites-dot that don't adhere to the modern {type, payload} format */
export type LegacySitesToAppMessage =
  | {
      requestLogout: unknown;
    }
  | {
      requestOrientation: 'landscape' | 'portrait';
    }
  | ({
      requestDirectSocialShare: true;
    } & DirectSocialSharePayload)
  | {
      action: 'url_preverification';
      statusCode: number;
    }
  | {
      type: 'webview_navigated';
      payload: unknown;
    };

export type RequestRecaptchaTokenSitesToAppMessage = {
  type: 'request_recaptcha_token';
  payload: {
    actionName: string;
  };
};

export type RequestAppSupportsFeature = {
  type: 'supports_feature';
  feature: NewFeature;
};

/** Messages sent by sites-dot to the mobile app. There is only one receiving channel in the mobile app,
 *   so channel is not a member of this data structure
 */
export type SitesToAppMessage =
  | ChannelUpdateSitesToAppMessage
  | RequestOpenExternalUrlSitesToAppMessage
  | ReceiveUserProfileSitesToAppMessage
  | RequestSocialAuthSitesToAppMessage
  | OpenMobileDebugSitesToAppMessage
  | RequestLogoutSitesToAppMessage
  | RequestDebugInfoSitesToAppMessage
  | NotifyUserAuthenticationStatusSiteToAppMessage
  | RequestScreenWakeLockSitesToAppMessage
  | LegacySitesToAppMessage
  | RequestRecaptchaTokenSitesToAppMessage
  | RequestAppSupportsFeature;
